import { SettingsAdjust16 } from "@carbon/icons-react";
import { Button } from "components/atoms/Button";
import { Stack } from "components/atoms/Stack";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { setFilterSidebarOpenStatus } from "store/tableSlice/index.slice";
import { useTheme } from "styled-components";
import { TableColumnToggle } from "../TableColumnToggle";
import { TableSearch } from "../TableSearch";
import { TableProps } from "../types";
import { TableControls } from "../TableControls";
import { IconButton } from "../../IconButton";
import { FaRedo, FaBarcode } from "react-icons/fa";
import { openModal } from "store/modalSlice";
import { ColumnsDropdown } from "components/molecules/TailwindsDropdowns/ColumnsDropdown";
import { TableFilterPills } from "../TableFilter/TableFilterPills";

export type ITableToolbarProps<D extends object> = Pick<
  TableProps<D>,
  | "filters"
  | "horizontalFilters"
  | "onSearchSubmit"
  | "searchInitialValue"
  | "tableKey"
  | "onRefresh"
  | "allowExport"
  | "recordCount"
  | "downloadOptions"
  | "TableButtons"
  | "userColumns"
  | "hideSearch"
  | "hideDownload"
  | "showScanAndPrint"
> & { useTableData: any; exportFunction: () => void };

export const TableToolbar = <D extends object>(
  props: ITableToolbarProps<D>
) => {
  const {
    filters,
    horizontalFilters,
    onSearchSubmit,
    searchInitialValue,
    tableKey,
    onRefresh,
    allowExport,
    recordCount,
    useTableData,
    downloadOptions,
    exportFunction,
    TableButtons,
    userColumns,
    hideSearch,
    hideDownload,
    showScanAndPrint,
  } = props;

  const dispatch = useDispatch();

  const { isFilterSidebarOpen } = useSelector((state: RootState) => {
    return state.tableSlice;
  });

  const theme = useTheme();

  return (
    <div
      className="relative flex items-center justify-between border-b h-16 max-md:overflow-x-auto scrollbar-none"
      style={{
        borderColor: theme.borderColor,
        paddingLeft: theme.gutter,
        paddingRight: theme.gutter,
      }}
    >
      <Stack spacing={4} direction="horizontal">
        {filters && horizontalFilters && <TableFilterPills filters={filters} />}
        {filters && !horizontalFilters && (
          <Button
            onClick={() => {
              dispatch(setFilterSidebarOpenStatus(!isFilterSidebarOpen));
            }}
          >
            <SettingsAdjust16 className="mr-2" />
            Filters
          </Button>
        )}
        {TableButtons ? <TableButtons /> : null}
      </Stack>

      <div className="flex flex-row items-center ml-auto">
        {onSearchSubmit && !hideSearch && (
          <div className="flex">
            <TableSearch
              records={recordCount || 0}
              handleSubmit={onSearchSubmit}
              initialValue={searchInitialValue}
            />
          </div>
        )}

        {allowExport && !hideDownload && (
          <div className="flex ml-4">
            <TableControls
              exportData={() => {
                exportFunction();
              }}
              downloadOptions={downloadOptions}
            />
          </div>
        )}
        {onRefresh && (
          <div className="ml-4">
            <IconButton
              icon={FaRedo}
              tooltip="Refresh shipments"
              onClick={() => {
                onRefresh();
              }}
            />
          </div>
        )}
        {showScanAndPrint && (
          <div className="ml-4">
            <IconButton
              icon={FaBarcode}
              tooltip="Scan & Print"
              onClick={() => {
                dispatch(openModal({ name: "scanAndPrint", size: "2xl" }));
              }}
            />
          </div>
        )}
        <div className="ml-4">
          <ColumnsDropdown
            {...useTableData}
            userColumns={userColumns ? userColumns : null}
            tableKey={tableKey}
          />
        </div>
      </div>
    </div>
  );
};
